import { useEffect, useRef, useState } from 'react';
import styles from './TakeAllSnapshots.module.css';

const TakeAllSnapshots = ({ videoElementIds, snapshotFileType, onSnapshot, id, imageQuality = 1, showPlaceholders = false }) => {
    const canvasRef = useRef();
    const [videoElements, setVideoElements] = useState([]);

    useEffect(() => {
        const elements = videoElementIds
            .map((id) => {
                const el = document.getElementById(id);
                if (!el) return null;
                const container = el.closest(`div[id="video-container__${id}"]`);
                return { el, container };
            })
            .filter((item) => item !== null && item.el && item.container);

        setVideoElements(elements);
    }, [videoElementIds]);

    useEffect(() => {
        if (videoElements.length === 0) return;

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        context.fillStyle = '#f0f0f0';
        context.fillRect(0, 0, canvas.width, canvas.height);

        const readyVideos = videoElements.filter(({ el }) => {
            return el && el.readyState >= 2 && el.videoWidth > 0 && el.videoHeight > 0;
        });
        const videosToProcess = showPlaceholders ? videoElements : readyVideos;
        const count = videosToProcess.length;

        if (count === 0) {
            onSnapshot(id, null);
            return;
        }

        let cols, rows;
        if (count <= 3) {
            cols = count;
            rows = 1;
        } else if (count <= 9) {
            cols = 3;
            rows = Math.ceil(count / 3);
        } else {
            cols = 4;
            rows = Math.ceil(count / 4);
        }

        const aspectRatio = 16 / 9;
        const cellWidth = canvas.width / cols;
        const cellHeight = cellWidth / aspectRatio;

        const totalHeight = rows * cellHeight;
        const verticalOffset = (canvas.height - totalHeight) / 2;

        videosToProcess.forEach(({ el: videoElement, container }, index) => {
            try {
                const col = index % cols;
                const row = Math.floor(index / cols);
                const xPos = col * cellWidth;
                const yPos = row * cellHeight + verticalOffset;
                const isReady = videoElement && videoElement.readyState >= 2 && videoElement.videoWidth > 0 && videoElement.videoHeight > 0;

                if (isReady) {
                    const intrinsicWidth = videoElement.videoWidth;
                    const intrinsicHeight = videoElement.videoHeight;

                    const videoRect = videoElement.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();

                    let angle = 0;
                    const style = videoElement.getAttribute('style');
                    if (style) {
                        const angleMatch = style.match(/rotate\((-?\d+)deg\)/);
                        if (angleMatch && angleMatch[1]) angle = parseInt(angleMatch[1], 10);
                    }

                    let scale, x, y, relativeWidth, relativeHeight, width, height;
                    if (angle === 270 || angle === 90) {
                        scale = intrinsicHeight / videoRect.width;
                        width = containerRect.width * scale || intrinsicWidth;
                        height = containerRect.height * scale || intrinsicHeight;
                        relativeWidth = height;
                        relativeHeight = width;
                        if (angle === 270) {
                            x = videoRect.y + videoRect.height - containerRect.y - containerRect.height;
                            y = containerRect.x - videoRect.x;
                        } else {
                            x = containerRect.y - videoRect.y;
                            y = videoRect.x + videoRect.width - containerRect.x - containerRect.width;
                        }
                    } else {
                        scale = intrinsicHeight / videoRect.height;
                        width = containerRect.width * scale || intrinsicWidth;
                        height = containerRect.height * scale || intrinsicHeight;
                        relativeHeight = height;
                        relativeWidth = width;
                        if (angle === 0) {
                            x = containerRect.x - videoRect.x;
                            y = containerRect.y - videoRect.y;
                        }
                        if (angle === 180) {
                            x = videoRect.x + videoRect.width - containerRect.x - containerRect.width;
                            y = videoRect.y + videoRect.height - containerRect.y - containerRect.height;
                        }
                    }

                    const drawWidth = cellWidth;
                    const drawHeight = cellHeight;

                    context.save();
                    context.translate(xPos + drawWidth / 2, yPos + drawHeight / 2);
                    if (angle !== 0) {
                        context.rotate((angle * Math.PI) / 180);
                    }

                    context.drawImage(
                        videoElement,
                        x * scale,
                        y * scale,
                        relativeWidth,
                        relativeHeight,
                        -drawWidth / 2,
                        -drawHeight / 2,
                        drawWidth,
                        drawHeight
                    );

                    context.restore();
                }

                context.lineWidth = 0;
                context.strokeRect(xPos, yPos, cellWidth, cellHeight);
            } catch (error) {
                console.error('Error capturing snapshot for video element:', error);
            }
        });

        try {
            const base64 = canvas.toDataURL(snapshotFileType || 'image/jpeg', imageQuality);
            onSnapshot(id, base64);
        } catch (error) {
            console.error('Error generating all snapshots:', error);
            onSnapshot(id, null);
        }
    }, [videoElements, onSnapshot, id, snapshotFileType, imageQuality, showPlaceholders]);

    const canvasWidth = 1920;
    const canvasHeight = 1080;

    return <canvas className={styles.canvas} ref={canvasRef} width={canvasWidth} height={canvasHeight}></canvas>;
};

export default TakeAllSnapshots;
